/* The one modal. Renders whatever is on top of the stack, in the
   page vocabulary every other popup here already uses -- pg-modal
   for the frame, lp-tabs for the tabs, lp-dialog-table for lists --
   so a bridged popup is indistinguishable from a hand-written one. */

import { Suspense, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft } from 'lucide-react';

import { useAuth } from '../../../../context/AuthContext';
import { useWaypoint } from './WaypointProvider.jsx';
import { getWaypoint } from './registry';
import './waypoint.css';

/* The profile-label match Header.jsx and Chat.jsx both use. Local, like
   theirs: there is no shared helper for this yet, and inventing one here
   would put the whole app's role vocabulary behind a popup component. */
const isStudentViewer = (user) =>
  Boolean(
    (user?.profiles || []).some(
      (p) => String(p?.label || p?.name || '').toLowerCase() === 'student',
    ),
  );

export default function WaypointHost() {
  const { stack, back, close, setTab } = useWaypoint();
  /* Above every early return below, and above every const that reads it:
     a hook cannot sit behind `if (!top) return null`. */
  const { user, hasPermission } = useAuth();
  const studentViewer = isStudentViewer(user);
  const top = stack[stack.length - 1] || null;

  const [entity, setEntity] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  /* Bumped per load so a slow request for a frame the reader has
     already left cannot land on top of the one they are looking at.
     Drilling in and straight back out is exactly how that happens. */
  const seq = useRef(0);

  const key = top ? `${top.type}:${top.id}` : null;

  /* The edit form over the popup (registry `edit`), and a count bumped
     after it saves so the popup loads the record again and shows the
     change. A different record closes the form. */
  const [editing, setEditing] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  useEffect(() => { setEditing(false); }, [key]);

  useEffect(() => {
    if (!top) {
      setEntity(null);
      setFailed(false);
      setLoading(false);
      return undefined;
    }

    const entry = getWaypoint(top.type);
    if (!entry) return undefined;

    const mine = ++seq.current;
    setLoading(true);
    setFailed(false);
    // Cleared, not kept: showing the previous entity's fields under
    // the new one's heading for a moment reads as wrong data rather
    // than as loading.
    setEntity(null);

    (async () => {
      try {
        const loaded = await entry.load(top.id);
        if (mine !== seq.current) return;
        setEntity(loaded || null);
        setFailed(!loaded);
      } catch (err) {
        if (mine !== seq.current) return;
        console.error('[waypoint] could not load', top.type, top.id, err);
        setFailed(true);
      } finally {
        if (mine === seq.current) setLoading(false);
      }
    })();

    return undefined;
    // key, not `top`: a tab change makes a new frame object but the
    // same record, and refetching on every tab click would blink the
    // whole panel.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, reloadTick]);

  if (!top) return null;

  const entry = getWaypoint(top.type);
  if (!entry) return null;

  const { View, label, tabs: declaredTabs = [], staffOnlyTabs = [] } = entry;

  /* Tabs a student may not have are not drawn, and not honoured when one
     arrives in the URL -- the stack is pasteable, so the strip is not the
     only way to ask for a tab. Falls back to the first tab that IS
     allowed rather than rendering the view with a tab it refuses. */
  const tabs = studentViewer
    ? declaredTabs.filter((t) => !staffOnlyTabs.includes(t))
    : declaredTabs;
  // `|| top.tab` for a type that declares no tabs at all: those views read
  // the frame's tab themselves and must not be handed undefined.
  const activeTab = tabs.includes(top.tab) ? top.tab : tabs[0] || top.tab;
  const parent = stack.length > 1 ? stack[stack.length - 2] : null;
  const parentEntry = parent ? getWaypoint(parent.type) : null;

  const title = entity ? entry.title(entity) : `Loading ${label.toLowerCase()}…`;
  const editEntry = entry.edit && entity && hasPermission?.(...entry.edit.permission) ? entry.edit : null;
  // Not tied to `entity`: the popup reloads while the form is still up
  // showing its done panel, and the form must not drop out meanwhile.
  const EditForm = entry.edit?.Form;

  /* Saved: the form shows its done panel and closes itself (PopupDone);
     meanwhile the popup reloads, and the page behind is told
     (`waypoint:saved`) so a list or grid showing the record can fetch
     it again. */
  const onEdited = () => {
    setReloadTick((n) => n + 1);
    window.dispatchEvent(new CustomEvent('waypoint:saved', { detail: { type: top.type, id: top.id } }));
  };
  const subtitle = entity && entry.subtitle ? entry.subtitle(entity) : null;

  return createPortal(
    <div
      className="pg-modal wp-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="wp-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div className="pg-modal-panel pg-modal-panel--wide wp-panel">
        <header className="pg-modal-head">
          <div className="pg-modal-heading">
            {/* The trail back, as one step rather than a full
                breadcrumb: the stack is usually two deep, and a
                breadcrumb of two is a back link with extra words. */}
            {parent && (
              <button type="button" className="wp-back" onClick={back}>
                <ChevronLeft size={14} />
                Back to {parentEntry ? parentEntry.label.toLowerCase() : 'previous'}
              </button>
            )}
            <span className="pg-modal-eyebrow">{label}</span>
            <h3 className="pg-modal-title" id="wp-title">
              {title}
            </h3>
            {subtitle && <p className="pg-modal-sub">{subtitle}</p>}
          </div>

          <button
            type="button"
            className="pg-modal-close"
            onClick={close}
            aria-label="Close"
          >
            &times;
          </button>
        </header>

        {tabs.length > 1 && (
          <div className="lp-tabs" role="tablist" aria-label={label}>
            {tabs.map((t) => (
              <button
                type="button"
                key={t}
                role="tab"
                id={`wp-tab-${t}`}
                aria-selected={activeTab === t}
                aria-controls="wp-panel"
                className={`lp-tab${activeTab === t ? ' is-on' : ''}`}
                onClick={() => setTab(t)}
              >
                {t.charAt(0).toUpperCase() + t.slice(1)}
              </button>
            ))}
          </div>
        )}

        <div className="pg-modal-body" role="tabpanel" id="wp-panel">
          {loading && (
            <div className="wp-state">
              <span className="pg-spinner" aria-hidden="true" />
              <p className="lp-muted">Loading…</p>
            </div>
          )}

          {!loading && failed && (
            <div className="wp-state">
              <p className="lp-muted">
                This {label.toLowerCase()} could not be loaded. It may have been
                deleted, or you may not have access to it.
              </p>
            </div>
          )}

          {!loading && !failed && entity && (
            <View id={top.id} entity={entity} tab={activeTab} />
          )}
        </div>

        <footer className="pg-modal-foot">
          {/* Edit is the popup's action when there is one, so it takes the
              main button and Close steps down to the plain one. */}
          {editEntry && (
            <div className="pgm-actions">
              <button type="button" className="pg-btn pg-btn--main" onClick={() => setEditing(true)}>
                {editEntry.label}
              </button>
            </div>
          )}
          <button type="button" className={editEntry ? 'pg-btn' : 'pg-btn pg-btn--main'} onClick={close}>
            Close
          </button>
        </footer>
      </div>
      {editing && EditForm && (
        <Suspense fallback={null}>
          <EditForm
            id={top.id}
            name={title}
            onCancel={() => setEditing(false)}
            onSaved={onEdited}
          />
        </Suspense>
      )}
    </div>,
    document.body,
  );
}
